/**
 * selection-resolver — pure, zero-DOM selection engine for the ProductSchema API.
 *
 * Unified navigation model:
 *   - Every navigable axis is an entry in schema.options[].
 *   - A value with `targetHandle` set → cross-product jump (navigate).
 *   - A value with `targetHandle` null → in-product selection (resolve a variant
 *     locally via the variants[] optionValues map).
 *   - Greying (presentation only) is decided in render.js from the value's
 *     `selectable` flag + merged availability, not here.
 *
 * All same-page transitions flow through resolveSelection().
 */

/**
 * Find the variant in `variants` whose optionValues exactly match `candidate`.
 * @param {object[]} variants
 * @param {Record<string,string>} candidate
 * @returns {object|null}
 */
export function findVariant(variants, candidate) {
  if (!candidate || !Array.isArray(variants)) return null;
  const keys = Object.keys(candidate);
  return (
    variants.find((v) => {
      const ov = v?.optionValues ?? {};
      if (Object.keys(ov).length !== keys.length) return false;
      return keys.every((k) => ov[k] === candidate[k]);
    }) ?? null
  );
}

/**
 * Score a variant by how many of its optionValues match the candidate.
 * @param {object} variant
 * @param {Record<string,string>} candidate
 * @returns {number}
 */
function matchScore(variant, candidate) {
  let score = 0;
  const ov = variant?.optionValues ?? {};
  for (const [k, v] of Object.entries(candidate ?? {})) {
    if (ov[k] === v) score += 1;
  }
  return score;
}

/**
 * Closest-qualifying fallback: pick the variant that matches the most options,
 * preserving declaration order as the tie-break.
 *
 * `pinnedOption` is the axis the shopper just clicked. Scoring alone treats it
 * as one match among many, so a click that also has to move a second axis can
 * tie with the variant already selected and resolve back to it, which reads as
 * a dead button. Restricting the pool to variants that actually carry the
 * clicked value keeps the click meaningful; when none do, the whole set is
 * scored as before.
 *
 * @param {object} schema
 * @param {Record<string,string>} candidate
 * @param {string} [pinnedOption]
 * @returns {{ variant: object, selection: Record<string,string>, fallbackFrom: Record<string,string> }|null}
 */
function fallback(schema, candidate, pinnedOption) {
  const pinnedValue = pinnedOption == null ? null : candidate[pinnedOption];
  const pinned =
    pinnedValue == null
      ? []
      : schema.variants.filter(
          (v) => (v.optionValues ?? {})[pinnedOption] === pinnedValue
        );
  const pool = pinned.length > 0 ? pinned : schema.variants;

  const scored = pool
    .map((v, i) => ({ v, score: matchScore(v, candidate), i }))
    .sort((a, b) => b.score - a.score || a.i - b.i);

  if (scored.length === 0) return null;

  const best = scored[0].v;
  return {
    variant: best,
    selection: { ...best.optionValues },
    fallbackFrom: candidate,
  };
}

/**
 * Locate an option value by option name + raw value.
 * @param {object} schema
 * @param {string} optionName
 * @param {string} rawValue
 * @returns {object|null}
 */
export function findValue(schema, optionName, rawValue) {
  const opt = schema.options.find((o) => o.name === optionName);
  return opt?.values.find((v) => v.rawValue === rawValue) ?? null;
}

/**
 * Does this product have a variant carrying `optionName === rawValue`?
 *
 * The engine-wide discriminator for "select locally vs navigate". Every Color
 * value carries a `targetHandle` (each color also lives on sibling products),
 * but colors with a local variant must select in-place rather than reload; only
 * values with no local variant (a different Size/Ropehold) are true jumps.
 *
 * @param {object} schema
 * @param {string} optionName
 * @param {string} rawValue
 * @returns {boolean}
 */
export function hasLocalVariantFor(schema, optionName, rawValue) {
  const variants = Array.isArray(schema?.variants) ? schema.variants : [];
  return variants.some((v) => (v.optionValues ?? {})[optionName] === rawValue);
}

/**
 * The anchor variant is the one whose SKU equals the schema id; its optionValues
 * describe the current on-page selection. Falls back to the first variant.
 * @param {object} schema
 * @returns {object|null}
 */
export function anchorVariant(schema) {
  return (
    schema.variants.find((v) => v.sku === schema.id) ??
    schema.variants[0] ??
    null
  );
}

/**
 * Seed selection from the anchor variant (what this product page represents).
 * Falls back to each option's current (non-navigating) value, then its first value.
 * @param {object} schema
 * @returns {Record<string,string>}
 */
export function initialSelection(schema) {
  const selection = {};
  if (!schema || !Array.isArray(schema.options)) return selection;

  const anchor = anchorVariant(schema);
  if (anchor?.optionValues) return { ...anchor.optionValues };

  for (const opt of schema.options) {
    const values = opt?.values ?? [];
    const current = values.find((v) => !v.targetHandle) ?? values[0];
    if (current) selection[opt.name] = current.rawValue;
  }
  return selection;
}

/**
 * Core resolution for a same-page option change.
 *
 * @param {object} schema
 * @param {Record<string,string>} currentSelection
 * @param {{ optionName: string, newValue: string }} change
 * @returns {{
 *   variant?: object,
 *   selection?: Record<string,string>,
 *   fallbackFrom?: Record<string,string>,
 *   redirect?: { handle: string, selection: Record<string,string> }
 * }|null}
 */
export function resolveSelection(schema, currentSelection, change) {
  const candidate = {
    ...currentSelection,
    [change.optionName]: change.newValue,
  };

  const value = findValue(schema, change.optionName, change.newValue);

  // A targetHandle now means the value lives in a different sub-product, so it
  // navigates. Per-partition emission gives the anchor's own values a null
  // targetHandle, so this no longer needs the hasLocalVariantFor guard (which
  // mis-routed a same-color swatch from another sub-product back to the local one).
  if (value?.targetHandle) {
    return { redirect: { handle: value.targetHandle, selection: candidate } };
  }

  const variant = findVariant(schema.variants, candidate);
  if (variant) return { variant, selection: candidate };

  return fallback(schema, candidate, change.optionName);
}

/**
 * Build the initial selection from URL `pf_` params layered on the anchor.
 *
 * A param is applied only when the product has a variant carrying its value — so
 * the clicked color highlights after a cross-product jump (colors always carry a
 * `targetHandle` yet exist locally on the destination), while a Size/Ropehold
 * param that navigates elsewhere is ignored. Variant existence, not
 * `targetHandle`, is the discriminator.
 *
 * @param {object} schema
 * @param {string} search  window.location.search
 * @returns {{ variant: object|null, selection: Record<string,string> }}
 */
export function hydrateFromUrl(schema, search) {
  const selection = initialSelection(schema) ?? {};
  const params = new URLSearchParams(search);
  const options = Array.isArray(schema?.options) ? schema.options : [];

  for (const opt of options) {
    const key = `pf_${opt.name.toLowerCase().replace(/\s+/g, '_')}`;
    const raw = params.get(key);
    if (raw == null) continue;

    const value = (opt.values ?? []).find((v) => v.rawValue === raw);
    if (!value) continue;

    // Apply only if a local variant carries this value (see above).
    if (!hasLocalVariantFor(schema, opt.name, raw)) continue;

    selection[opt.name] = raw;
  }

  const variant =
    findVariant(schema.variants, selection) ??
    fallback(schema, selection)?.variant ??
    null;
  return { variant, selection };
}

/**
 * Project a prior selection onto a target schema. Retained for callers that
 * have the target schema available (e.g. multi-picker pages).
 *
 * @param {Record<string,string>} prevSelection
 * @param {object} targetSchema
 * @returns {Record<string,string>}
 */
export function projectSelection(prevSelection, targetSchema) {
  const result = {};

  for (const opt of targetSchema.options) {
    const validValues = new Set(opt.values.map((v) => v.rawValue));
    const prev = prevSelection[opt.name];
    if (prev != null && validValues.has(prev)) {
      result[opt.name] = prev;
    }
  }

  const unfilled = targetSchema.options.filter((o) => result[o.name] == null);
  for (const opt of unfilled) {
    for (const val of opt.values) {
      const candidate = { ...result, [opt.name]: val.rawValue };
      if (findVariant(targetSchema.variants, candidate) != null) {
        result[opt.name] = val.rawValue;
        break;
      }
    }
    if (result[opt.name] == null && opt.values.length > 0) {
      result[opt.name] = opt.values[0].rawValue;
    }
  }

  return result;
}
